import React from 'react'
import {
  TagPicker,
  ITag,
  ITagPickerProps,
  TagItemSuggestion,
  TagItem,
  TooltipHost,
} from 'office-ui-fabric-react'
import './TagInput.css'

interface InputProps extends ITagPickerProps {
  label: string
  required?: boolean
  onResolveSuggestions: (
    filter: string,
    selectedItems?: ITag[] | undefined
  ) => ITag[] | PromiseLike<ITag[]>
  onChange: (value?: ITag[]) => any
  selectedItems: ITag[]
  disabled?: boolean
  errorMessage?: string
  pickerLabels: {
    suggestionsHeader: string
    noResults: string
  }
  itemLimit?: number
  groupByKey?: boolean
}

export function TagInput({
  errorMessage,
  label,
  required,
  onResolveSuggestions,
  pickerLabels,
  selectedItems,
  onChange,
  itemLimit,
  disabled,
  groupByKey,
  ...props
}: InputProps) {
  const resolveSuggestionsToGroupedTags = async (
    filter: string,
    selectedItems?: ITag[]
  ) => {
    const suggestions = await onResolveSuggestions(filter, selectedItems)
    if (!groupByKey) {
      return suggestions
    }
    const groupToItems = suggestions.reduce(
      (acc, item) => {
        const group = item.name.split('/')[0]
        acc[group] = acc[group] || []
        acc[group].push(item)
        return acc
      },
      {} as Record<string, ITag[]>
    )
    const groupedTags = Object.entries(groupToItems)
      .filter(([_, items]) => items.length > 0)
      .flatMap(([groupTitle, items]) => [
        {
          key: `group_${Math.random()}`,
          name: groupTitle,
        },
        ...items,
      ])
    return groupedTags
  }
  return (
    <div className={'tag-input' + (errorMessage ? ' has-error' : '')}>
      <div
        style={{
          marginTop: 5,
          marginBottom: 5,
          fontWeight: 600,
          color: 'var(--color-text-primary)',
        }}
      >
        {label}
        {required && <span style={{ color: 'var(--color-error)' }}> *</span>}
      </div>
      <TagPicker
        {...props}
        onResolveSuggestions={resolveSuggestionsToGroupedTags}
        pickerSuggestionsProps={{
          suggestionsHeaderText: pickerLabels.suggestionsHeader,
          noResultsFoundText: pickerLabels.noResults,
        }}
        onEmptyInputFocus={() =>
          resolveSuggestionsToGroupedTags('', selectedItems)
        }
        onChange={onChange}
        selectedItems={selectedItems}
        itemLimit={itemLimit || 10}
        disabled={disabled}
        inputProps={{ 'aria-label': label }}
        onRenderItem={({ item, ...props }) => (
          <TooltipHost
            content={`${isPlacement(item) ? 'Placement' : 'Tag'}: ${item.name}`}
          >
            <TagItem
              item={item}
              {...props}
              className={`tag-item ${isPlacement(item) ? 'plcmt' : 'tag'}`}
            >
              {item.name}
            </TagItem>
          </TooltipHost>
        )}
        onRenderSuggestionsItem={(item) =>
          item.key.startsWith('group_') ? (
            <div
              className="tag-group"
              onClick={(event) => {
                event.stopPropagation()
              }}
            >
              {item.name}
            </div>
          ) : (
            <TagItemSuggestion>
              <span
                className={`suggestion-span ${isPlacement(item) ? 'plcmt' : 'tag'}`}
              >
                {item.name}
              </span>
            </TagItemSuggestion>
          )
        }
      />
      {errorMessage && (
        <span data-automation-id="error-message">{errorMessage}</span>
      )}
    </div>
  )
}

function isPlacement(item: ITag) {
  return item.name.split('/').length > 2
}
